import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MouseIcon from '@mui/icons-material/Mouse';
import CampaignIcon from '@mui/icons-material/Campaign';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import {
  FollowupEvaluationSummary,
  FollowupEvaluationCampaign,
} from '../../helpers/webinarMailingData';
import moment from 'moment';

// Summary Card Component
const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  highlight?: boolean;
}> = ({ title, value, icon, color = '#1976d2', highlight = false }) => (
  <Card
    sx={{
      height: '100%',
      border: highlight ? '2px solid #2e7d32' : 'none',
      backgroundColor: highlight ? '#f1f8e9' : 'white',
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color={highlight ? '#2e7d32' : 'inherit'}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: highlight ? '#2e7d32' : color, opacity: 0.7, fontSize: 40 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const FollowupEvaluationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<FollowupEvaluationSummary | null>(null);
  const [campaigns, setCampaigns] = useState<FollowupEvaluationCampaign[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load summary and campaigns in parallel
      const [summaryRes, campaignsRes] = await Promise.all([
        fetchData<FollowupEvaluationSummary>('/api/webinar-followups/evaluation/summary'),
        fetchData<FollowupEvaluationCampaign[]>('/api/webinar-followups/evaluation/campaigns'),
      ]);

      if (summaryRes.success && summaryRes.data) {
        setSummary(summaryRes.data);
      }
      if (campaignsRes.success && campaignsRes.data) {
        setCampaigns(campaignsRes.data);
      }

      if (!summaryRes.success || !campaignsRes.success) {
        setError('Fehler beim Laden der Auswertung');
      }
    } catch (err) {
      setError('Fehler beim Laden der Auswertung');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatCurrency = (value: number) => `${formatNumber(value, 2)} EUR`;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Nachfass-Auswertung
      </Typography>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Kampagnen"
              value={formatNumber(summary.totalCampaigns, 0)}
              icon={<CampaignIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Empfänger"
              value={formatNumber(summary.totalRecipients, 0)}
              icon={<PeopleIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Conversions"
              value={formatNumber(summary.totalConversions, 0)}
              icon={<ShoppingCartIcon fontSize="inherit" />}
              highlight={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Umsatz (bezahlt)"
              value={formatCurrency(summary.totalRevenue)}
              icon={<AttachMoneyIcon fontSize="inherit" />}
              highlight={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø Öffnungsrate"
              value={formatPercent(summary.avgOpenRate)}
              icon={<VisibilityIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø CTR"
              value={formatPercent(summary.avgCtr)}
              icon={<MouseIcon fontSize="inherit" />}
            />
          </Grid>
        </Grid>
      )}

      {/* Campaign Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Kampagne
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Webinar
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="center"
              >
                Segmente
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Empfänger
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Opens
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Open%
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                Clicks
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                align="right"
              >
                CTR
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold', color: '#2e7d32' }}
                align="right"
              >
                Conversions
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold', color: '#2e7d32' }}
                align="right"
              >
                Umsatz (bezahlt)
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Erstellt
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  Keine Kampagnen mit gesendeten Segmenten gefunden
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                      {campaign.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {campaign.webinarTitle || '-'}
                    </Typography>
                    {campaign.webinarDate && (
                      <Typography variant="caption" color="textSecondary">
                        {moment(campaign.webinarDate).format('DD.MM.YYYY')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{campaign.segmentsSent}</TableCell>
                  <TableCell align="right">{formatNumber(campaign.recipients, 0)}</TableCell>
                  <TableCell align="right">{formatNumber(campaign.opens, 0)}</TableCell>
                  <TableCell align="right">{formatPercent(campaign.openRate)}</TableCell>
                  <TableCell align="right">{formatNumber(campaign.clicks, 0)}</TableCell>
                  <TableCell align="right">{formatPercent(campaign.ctr)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    {formatNumber(campaign.conversions, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    {formatCurrency(campaign.revenue)}
                  </TableCell>
                  <TableCell>{moment(campaign.createdAt).format('DD.MM.YYYY')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FollowupEvaluationTab;
